import { readCompetencyView } from './competencyView';
import { EvolutionJourneySection } from './EvolutionJourneySection';
import { FirstEvaluationSection } from './FirstEvaluationSection';
import { StrengthsSection } from './StrengthsSection';
import { WithheldLevelsNotice, WithheldLevelsSection } from './WithheldLevelsSection';

import type { CompetencyView } from './competencyView';
import type { ComponentType } from 'react';

import { usePortalEvolution } from '@/pages/EmployeePortal/hooks/usePortalReads';

/**
 * The section that stands in for both when no level exists to draw: one wait per cause, and the
 * views that do have levels are absent from here. The path to a first assessment answers two of
 * those causes, opening on the stop the employee actually stands on.
 */
const SOLE_SECTION: Partial<Record<CompetencyView, ComponentType>> = {
    wait: WithheldLevelsSection,
    firstEvaluation: FirstEvaluationSection,
    awaitingProfile: () => <FirstEvaluationSection isProfileAwaited />,
};

/**
 * What the employee's own competencies say about them: what they excel at and how far along their
 * journey they are -- or, when no level of theirs exists to read, the reason there is none.
 *
 * The decision is made here rather than in either section, because it is a decision about which
 * sections exist. Until the journey answers, the sections are drawn as they always were: each says
 * it is loading, and none says it is empty.
 */
export const CompetencySections = () => {
    const { data } = usePortalEvolution();
    const view = data ? readCompetencyView(data) : 'levels';
    const SoleSection = SOLE_SECTION[view];

    if (SoleSection) {
        return <SoleSection />;
    }

    return (
        <>
            {view === 'levelsAndWait' && <WithheldLevelsNotice />}
            <StrengthsSection />
            <EvolutionJourneySection />
        </>
    );
};
